import React, { useState } from "react";
import {
  AiFillGithub,
  AiFillLinkedin,
  AiOutlineInstagram,
  AiOutlineTwitter,
  AiOutlineYoutube,
} from "react-icons/ai";
import { FaTiktok, FaTwitch } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import useAnimationSettings from "../../hooks/useAnimationSettings";

const BG_SOFT = "rgba(248,242,234,0.96)";
const VIOLETA = "#697B68";
const ROSA = "#E09767";

export default function Redes() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const {
    reduceMotion,
    compactMotion,
    allowHoverAnimations,
    allowAmbientAnimations,
    durations,
  } = useAnimationSettings();

  if (pathname === "/links") return null;

  const links = [
    {
      href: "https://www.youtube.com/channel/UCjnn1S2FKvvkicwEuoBlmAQ",
      Icon: AiOutlineYoutube,
      label: "YouTube",
    },
    {
      href: "https://www.tiktok.com/@sebassdev",
      Icon: FaTiktok,
      label: "TikTok",
      size: 24,
    },
    {
      href: "https://www.instagram.com/_sebasdev/",
      Icon: AiOutlineInstagram,
      label: "Instagram",
    },
    {
      href: "https://www.twitch.tv/sebassdev",
      Icon: FaTwitch,
      label: "Twitch",
      size: 28,
    },
    {
      href: "https://twitter.com/_sebassotelo",
      Icon: AiOutlineTwitter,
      label: "Twitter/X",
    },
    {
      href: "https://github.com/Sebassotelo",
      Icon: AiFillGithub,
      label: "GitHub",
    },
  ];

  const desktopEnter = reduceMotion ? false : { x: -80, opacity: 0 };
  const tapFeedback = reduceMotion ? undefined : { scale: 0.95 };
  const listItemVariants = reduceMotion
    ? {
        hidden: { opacity: 1, x: 0, scale: 1 },
        show: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 1, x: 0, scale: 1 },
      }
    : {
        hidden: { opacity: 0, x: -16, scale: 0.94 },
        show: (index) => ({
          opacity: 1,
          x: 0,
          scale: 1,
          transition: {
            delay: index * (compactMotion ? 0.03 : 0.05),
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        }),
        exit: {
          opacity: 0,
          x: -16,
          scale: 0.94,
          transition: { duration: durations.hover },
        },
      };

  return (
    <>
      <motion.div
        initial={desktopEnter}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          delay: reduceMotion ? 0 : compactMotion ? 0.2 : 0.4,
          type: "spring",
          stiffness: 110,
        }}
        className="motion-gpu fixed left-0 top-[30vh] z-[100] hidden flex-col items-center gap-2 rounded-r-2xl p-3 backdrop-blur-md 2xl:flex"
        style={{
          backgroundColor: BG_SOFT,
          boxShadow:
            "0 8px 32px 0 rgba(105,123,104,0.15), 0 2px 8px 0 rgba(105,123,104,0.08)",
        }}
      >
        <motion.a
          href="https://www.linkedin.com/in/sebassotelo/"
          target="_blank"
          rel="noreferrer"
          aria-label="LinkedIn"
          className="motion-gpu relative group"
          whileHover={allowHoverAnimations ? { scale: 1.1 } : undefined}
          whileTap={tapFeedback}
        >
          <motion.div
            className={`absolute inset-0 rounded-lg opacity-0 blur-xl transition-opacity ${
              allowHoverAnimations ? "group-hover:opacity-25" : ""
            }`}
            style={{
              background: `linear-gradient(135deg, ${ROSA}, transparent)`,
            }}
          />
          <motion.div
            animate={
              allowAmbientAnimations ? { rotate: [0, -8, 8, -8, 0] } : undefined
            }
            transition={
              allowAmbientAnimations
                ? {
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut",
                  }
                : undefined
            }
          >
            <AiFillLinkedin
              className="relative z-10 text-[2.25rem] transition-all duration-300"
              style={{
                color: VIOLETA,
                filter: "drop-shadow(0 2px 4px rgba(224,151,103,0.35))",
              }}
            />
          </motion.div>
          <span className="absolute -right-2 -top-1 flex h-3 w-3">
            {allowAmbientAnimations && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full"
                style={{ background: ROSA, opacity: 0.75 }}
              />
            )}
            <span
              className="relative inline-flex h-3 w-3 rounded-full"
              style={{ background: ROSA }}
            />
          </span>
        </motion.a>

        <div
          className="my-1 h-px w-full"
          style={{
            background: `linear-gradient(90deg, transparent, ${VIOLETA}55, transparent)`,
          }}
        />

        {links.map(({ href, Icon, label, size }) => (
          <motion.a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="motion-gpu relative group"
            whileHover={
              allowHoverAnimations ? { scale: 1.12, rotate: 4 } : undefined
            }
            whileTap={reduceMotion ? undefined : { scale: 0.9 }}
          >
            <motion.div
              className={`absolute inset-0 rounded-lg opacity-0 blur-lg transition-opacity ${
                allowHoverAnimations ? "group-hover:opacity-30" : ""
              }`}
              style={{ background: ROSA }}
            />
            <Icon
              className={`relative z-10 transition-all duration-300 ${
                allowHoverAnimations ? "group-hover:brightness-110" : ""
              }`}
              style={{
                fontSize: `${(size || 32) / 16}rem`,
                color: VIOLETA,
                filter: "drop-shadow(0 1px 2px rgba(105,123,104,0.25))",
              }}
            />
          </motion.a>
        ))}
      </motion.div>

      <div className="relative z-[110] flex items-center justify-center gap-4 px-5 py-6 2xl:hidden">
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 -z-10 backdrop-blur-sm"
                style={{ background: "rgba(0,0,0,0.2)" }}
                onClick={() => setOpen(false)}
              />

              <motion.ul
                initial="hidden"
                animate="show"
                exit="exit"
                className="fixed left-1/2 top-1/2 z-20 grid max-h-[calc(100dvh-4rem)] w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 grid-cols-[repeat(auto-fit,minmax(3.25rem,1fr))] justify-items-center gap-3 overflow-y-auto rounded-2xl border border-[#697B68]/25 p-4 shadow-xl"
                style={{ backgroundColor: BG_SOFT }}
              >
                {links.map(({ href, Icon, label, size }, index) => (
                  <motion.li
                    key={label}
                    custom={index}
                    variants={listItemVariants}
                  >
                    <motion.a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      whileHover={allowHoverAnimations ? { scale: 1.05 } : undefined}
                      whileTap={tapFeedback}
                      className="motion-gpu relative grid h-[3.25rem] w-[3.25rem] place-items-center rounded-full backdrop-blur-md"
                      style={{
                        backgroundColor: BG_SOFT,
                        boxShadow:
                          "0 4px 16px rgba(105,123,104,0.18), 0 2px 4px rgba(105,123,104,0.12)",
                      }}
                      onClick={() => setOpen(false)}
                    >
                      <motion.div
                        className={`absolute inset-0 rounded-full opacity-0 transition-opacity ${
                          allowHoverAnimations ? "hover:opacity-25" : ""
                        }`}
                        style={{ background: ROSA }}
                      />
                      <Icon
                        className="relative z-10 transition-colors duration-200"
                        style={{
                          fontSize: `${(size || 26) / 16}rem`,
                          color: VIOLETA,
                          filter: "drop-shadow(0 1px 2px rgba(105,123,104,0.18))",
                        }}
                      />
                    </motion.a>
                  </motion.li>
                ))}
              </motion.ul>
            </>
          )}
        </AnimatePresence>

        <motion.button
          aria-label={open ? "Cerrar redes" : "Abrir redes"}
          onClick={() => setOpen((value) => !value)}
          whileHover={allowHoverAnimations ? { scale: 1.05 } : undefined}
          whileTap={tapFeedback}
          className="motion-gpu relative grid h-14 w-14 place-items-center overflow-hidden rounded-full focus:outline-none"
          style={{
            backgroundColor: VIOLETA,
            color: "white",
            boxShadow: open
              ? "0 8px 24px rgba(105,123,104,0.45), 0 4px 8px rgba(105,123,104,0.25)"
              : "0 4px 16px rgba(105,123,104,0.35), 0 2px 4px rgba(105,123,104,0.22)",
          }}
        >
          <motion.div
            className="absolute inset-0"
            animate={{
              background: open
                ? `linear-gradient(135deg, ${ROSA}, transparent)`
                : "transparent",
            }}
          />
          <motion.span
            animate={
              reduceMotion ? undefined : { rotate: open ? 180 : 0, scale: open ? 1.1 : 1 }
            }
            transition={
              reduceMotion
                ? undefined
                : { type: "spring", stiffness: 400, damping: 26 }
            }
            className="relative z-10 text-2xl leading-none"
          >
            &uarr;
          </motion.span>
        </motion.button>

        <motion.a
          href="https://www.linkedin.com/in/sebassotelo/"
          target="_blank"
          rel="noreferrer"
          aria-label="LinkedIn"
          whileHover={allowHoverAnimations ? { scale: 1.05 } : undefined}
          whileTap={tapFeedback}
          className="motion-gpu relative grid h-14 w-14 place-items-center overflow-hidden rounded-full backdrop-blur-md"
          style={{
            backgroundColor: BG_SOFT,
            boxShadow:
              "0 6px 20px rgba(224,151,103,0.25), 0 2px 4px rgba(105,123,104,0.12)",
          }}
        >
          <motion.div
            className="absolute inset-0 opacity-15"
            style={{
              background: `linear-gradient(135deg, ${ROSA}, transparent)`,
            }}
          />
          <motion.div
            animate={
              allowAmbientAnimations
                ? {
                    rotate: [0, -8, 8, -8, 0],
                    scale: [1, 1.05, 1.05, 1.05, 1],
                  }
                : undefined
            }
            transition={
              allowAmbientAnimations
                ? {
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 1.5,
                    ease: "easeInOut",
                  }
                : undefined
            }
          >
            <AiFillLinkedin
              className="relative z-10 text-[2.125rem]"
              style={{
                color: VIOLETA,
                filter: "drop-shadow(0 2px 4px rgba(224,151,103,0.35))",
              }}
            />
          </motion.div>
          <span className="absolute -right-1 -top-1 z-20 flex h-3.5 w-3.5">
            {allowAmbientAnimations && (
              <span
                className="absolute inline-flex h-full w-full animate-ping rounded-full"
                style={{ background: ROSA, opacity: 0.75 }}
              />
            )}
            <span
              className="relative inline-flex h-3.5 w-3.5 rounded-full ring-2 ring-white"
              style={{ background: ROSA }}
            />
          </span>
        </motion.a>
      </div>
    </>
  );
}
